import $ from 'jquery';
import _ from 'underscore';
import Promise from 'bluebird';
import { helpers } from '@bingads-webui/projection-grid-toolbar';
import { crossPageSelectHeadRow } from '@bingads-webui/projection-grid-cross-page-select-all';
import placeholderTemplate from '../../template/grid-nodata-placeholder.pug';
import { getGridColumnsConfig, getColumnChooserPluginConfig, isEveryThingElseRow, getSortReturn } from './product-groups-columns';
import { bidType, productGroupLevel } from '../../constant';

export class ProductGroupsGridConfig {
  constructor({
    deps,
    view,
    dataSource,
    isTreeView,
    onTogglePillClicked,
  }) {
    this.deps = deps;
    this.isTreeView = isTreeView;
    this.IsBidLessThan5CentsEnabled = _.result(this.deps.config.get('Permissions'), 'IsBidLessThan5CentsEnabled');
    this.IsCPGForBSCEnabled = _.result(this.deps.config.get('Permissions'), 'IsCPGForBSCEnabled');
    this.onTogglePillClicked = onTogglePillClicked;
    this.ReportEmailRule = deps.state.get('$ReportEmailRule');
    deps.state.unset('$ReportEmailRule');
    _.extend(this, {
      name: 'newProductGroupsGrid',
      tableClasses: ['table', 'table-bordered'],
      filterService: view.filterService,
      dataSource,
      scrolling: {
        virtualized: true,
        header: {
          type: 'sticky',
          offset() {
            return $('nav.navbar-default.navbar-fixed-top').height();
          },
        },
      },
      rows: {
        headRows: [
          crossPageSelectHeadRow(),
          'column-header-rows',
        ],
        bodyRows: [{
          type: 'data-rows',
          classes: ['product-partition-row'],
        }],
      },
      columns: getGridColumnsConfig(deps, view, isTreeView),
      selection: this.isTreeView ? {
        selectable: (item) => {
          if (_.isEmpty(item)) {
            return true;
          }
          return item && item.isAggregateRow !== true;
        },
      } : true,
      plugins: this.gridPluginConfig(view),
    });
  }

  gridToolBarConfig = view => helpers.singleToolbar({
    items: _.compact([{
      type: 'dropdown',
      button: {
        classes: ['btn', 'btn-hero'],
        text: this.deps.i18n.getString('GridToolbar_FilterBar_Edit'),
      },
      menu: {
        items: _.compact([view.isBidValueOption ? {
          text: this.deps.i18n.getString('ProductGroups_BidChanges'),
          onClick: () => {
            view.editBids(bidType.bidValue);
          },
        } : undefined, view.isBidBoostOption && this.IsCPGForBSCEnabled ? {
          text: this.deps.i18n.getString('ProductGroupsGrid_BidBoostChanges'),
          onClick: () => {
            view.editBids(bidType.bidBoost);
          },
        } : undefined]),
      },
    }, {
      type: 'button',
      id: 'button-download',
    }, {
      type: 'dropdown',
      id: 'dropdown-column-chooser',
    }, view.level === productGroupLevel.AdGroup ? {
      type: 'pill-control',
      id: 'view-toggle',
      selectedIndex: this.isTreeView ? 0 : 1,
      onClick: this.onTogglePillClicked,
      items: [{
        classes: 'glyph-bulleted-tree',
        text: '',
        title: this.deps.i18n.getString('ProductGroupsGrid_HierarchyView'),
      }, {
        classes: 'glyph-bulleted-list',
        text: '',
        title: this.deps.i18n.getString('ProductGroupsGrid_ListView'),
      }],
    } : undefined]),
  });

  processReportRule = (ReportRule) => {
    // Remove Warnings column in Headers
    // Warnings column will always be the third element in the Array
    const warningColumnHeaderIdx = ReportRule.Headers.indexOf(this.deps.i18n.getString('ProductGroupsGrid_ColumnName_Warnings'));
    if (warningColumnHeaderIdx > 0) {
      ReportRule.Headers.splice(warningColumnHeaderIdx, 1);
    }
    return _.defaults({
      Selection: _.defaults({
        IsAdGroupTreeView: this.isTreeView,
        QueryString: ReportRule.Selection.QueryString.replace(',Warnings', ''),
      }, ReportRule.Selection),
    }, ReportRule);
  };

  gridPluginConfig = view => ({
    inlineDownload: {
      DownloadView: this.deps.DownloadView,
      isAvailable: true,
      i18n: this.deps.i18n,
      s$i18n: this.deps.s$i18n,
      schema: this.deps.s$ProductGroup,
      gridId: 'newProductGroupsGrid',
      ReportEmailRule: this.ReportEmailRule,
      processReportRule: this.processReportRule,
    },
    uiBlocker: {
      el: $('#summary_tab_product_groups'),
    },
    placeholder: {
      filteredEmptyText: this.deps.i18n.getString('Grid_NoFilteredRecords'),
      container: '.grid-nodata-container',
      template: placeholderTemplate,
      templateOptions: {
        i18n: this.deps.i18n,
        shouldShowCreateRootLink: !!view.deps.pageContext.CurrentAdGroup,
      },
      asyncEmptyText: view.tooMuchDataErrorText,
      asyncCallback: () => Promise.resolve(view.showTooMuchDataError),
    },
    tree: {
      toggleColumnName: 'ProductGroup',
      templateDecorator: celTemplate => ({ value }) => {
        if (!_.isEmpty(value.total)) {
          return celTemplate({
            total: value.total,
          });
        }
        // hack to get parent item
        const parentItem = view.grid.gridView.itemWithKey(value.ParentId);
        let attributeText = value.Condition.value.Attribute;
        const isRoot = _.isEmpty(value.Condition.value.Operand);

        if (isRoot) {
          // Root node
          attributeText = this.deps.i18n.getString('ShoppingCampaign_AllProducts');
        } else if (_.isEmpty(value.Condition.value.Attribute)) {
          if (!parentItem.Condition.Attribute && parentItem.Condition.Operand) {
            // Parent node is everything else
            attributeText = this.deps.i18n.getString('ProductGroupsGrid_EverythingElse');
          } else {
            // Everything else in `parent's attribute`
            const parentAttribute = parentItem.Condition.Attribute || this.deps.i18n.getString('ShoppingCampaign_AllProducts');

            attributeText = this.deps.i18n.getString('ProductGroupsGrid_EverythingElseIn').replace('{0}', parentAttribute);
          }
        }

        return celTemplate(_.defaults({ i18n: this.deps.i18n }, value.Condition, {
          attributeText,
          isRoot,
        }));
      },
    },
    performanceLog: {
      appConfig: this.deps.config,
      logger: this.deps.logger,
    },
    toolbar: this.gridToolBarConfig(view),
    columnChooser: getColumnChooserPluginConfig(this.deps, view, this.isTreeView),
    clientSideSort: {
      compareMaker: (sortIteratee, direction, columnKey) => (item1, item2) => {
        // if current sorting column is product group name
        if (columnKey === 'Condition/Attribute') {
          // direction: -1 for desc, 1 for asc
          if (isEveryThingElseRow(item1)) {
            return 1;
          }

          if (isEveryThingElseRow(item2)) {
            return -1;
          }
        }

        const sortReturn =
          getSortReturn(
            sortIteratee(item1, direction),
            sortIteratee(item2, direction), columnKey
          );
        return sortReturn;
      },
      setTopToMax: true,
    },
    dateFilter: {
      state: this.deps.state,
      addDateChangeListener: this.deps.addDateChangeListener,
      edm: this.deps.edmCampaign,
      isOdataActionSource: true,
      comparisonColumns: [],
    },
    pagination: !this.isTreeView ? {
      availablePageSizes: [20, 50, 100, 200],
      enableRandomPage: false,
      preferenceService: this.deps.preferenceService,
    } : undefined,
    crossPageSelectAll: !this.isTreeView ? { i18n: this.deps.i18n } : undefined,
    editable: {
      i18n: this.deps.i18n,
    },
  });
}
